import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import AbstractVisual from '../AbstractVisual/AbstractVisual'
import { getService } from '../../data/services'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import './ServiceSpotlight.scss'

// Headlines for the dark spotlight carousel. `highlight` is the phrase painted
// with the gradient; it must appear verbatim in `headline`.
//
// These English strings are the fallback. Locale files translate each slide
// with the keys spot<Set><n>Name / spot<Set><n>Head / spot<Set><n>Hi
// (e.g. spotHome1Head), where n is the 1-based position in the set.
//
// Each page gets its own set — the homepage and /services feature different
// services with different copy, so no headline appears on two pages.
const SLIDE_SETS = {
  services: [
    { slug: 'generative-ai', headline: 'Language models wired into the work your team already does', highlight: 'the work your team already does' },
    { slug: 'rag', headline: 'Ask your company documents a question, get a cited answer', highlight: 'a cited answer' },
    { slug: 'ai-agents', headline: 'Software agents for the busywork between your systems', highlight: 'the busywork' },
    { slug: 'computer-vision', headline: 'Scans, photos and video your software can actually read', highlight: 'actually read' },
    { slug: 'mobile-development', headline: 'Apps for iPhone and Android from a single codebase', highlight: 'a single codebase' },
    { slug: 'ai-consulting', headline: 'Find out which AI idea deserves the budget first', highlight: 'deserves the budget' },
  ],
  home: [
    { slug: 'ai-development', headline: 'Forecasts and predictions trained on your own history', highlight: 'your own history' },
    { slug: 'nlp-chatbots', headline: 'A support assistant fluent in your customers’ languages', highlight: 'your customers’ languages' },
    { slug: 'data-engineering', headline: 'One trustworthy source for every number in the business', highlight: 'every number' },
    { slug: 'web-development', headline: 'Web platforms built for search, speed and real traffic', highlight: 'search, speed and real traffic' },
    { slug: 'mlops', headline: 'Models that keep performing long after launch day', highlight: 'long after launch day' },
    { slug: 'ui-ux-design', headline: 'Interfaces that make machine decisions easy to trust', highlight: 'easy to trust' },
  ],
}

const AUTOPLAY_MS = 7000

const pad = (n) => String(n).padStart(2, '0')

const Headline = ({ text, highlight }) => {
  const at = text.indexOf(highlight)
  if (!highlight || at === -1) return text
  return (
    <>
      {text.slice(0, at)}
      <span className='spot-gradient'>{highlight}</span>
      {text.slice(at + highlight.length)}
    </>
  )
}

/**
 * Full-width dark carousel: a headline on the left, the service's 3D artwork
 * morphing on the right, arrows and a 01 / 06 counter underneath.
 *
 * Autoplay pauses on hover, on keyboard focus, while the section is off
 * screen, and entirely for reduced-motion visitors. The first slide renders
 * in its final state (AnimatePresence initial={false}), so nothing starts
 * hidden.
 */
const ServiceSpotlight = ({ set = 'services', className = '' }) => {
  const reduced = useReducedMotion()
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [inView, setInView] = useState(false)
  const sectionRef = useRef(null)

  const { translations = {} } = useContext(LanguageContext) || {}
  const t = (key, fallback) => translations[key] || fallback
  const setKey = SLIDE_SETS[set] ? set : 'services'
  const prefix = `spot${setKey.charAt(0).toUpperCase()}${setKey.slice(1)}`

  const slides = SLIDE_SETS[setKey]
    .map((slide, i) => {
      const service = getService(slide.slug)
      if (!service) return null
      const key = `${prefix}${i + 1}`
      const headline = t(`${key}Head`, slide.headline)
      const highlight = t(`${key}Hi`, slide.highlight)
      return {
        ...slide,
        service,
        name: t(`${key}Name`, service.name),
        headline,
        // A translated highlight that no longer appears in its headline would
        // silently lose the gradient; fall back to no highlight instead.
        highlight: headline.includes(highlight) ? highlight : '',
      }
    })
    .filter(Boolean)
  const count = slides.length
  const slide = slides[index]

  const go = useCallback((step) => setIndex((current) => (current + step + count) % count), [count])

  useEffect(() => {
    if (!sectionRef.current) return undefined
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.3 })
    observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (reduced || paused || !inView) return undefined
    const timer = setTimeout(() => go(1), AUTOPLAY_MS)
    return () => clearTimeout(timer)
  }, [index, reduced, paused, inView, go])

  if (!slide) return null

  return (
    <section
      ref={sectionRef}
      className={`spot ${className}`}
      aria-roledescription='carousel'
      aria-label={t('spotAria', 'Featured services')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <AbstractVisual className='spot-visual' shape={slide.service.visual.shape} colors={slide.service.visual.colors} />
      <div className='spot-fade' aria-hidden='true' />

      <div className='spot-inner'>
        <div className='spot-copy' aria-live='polite'>
          <AnimatePresence mode='wait' initial={false}>
            <motion.div
              key={slide.slug}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className='spot-eyebrow'>{slide.name}</p>
              <h2 className='spot-title'>
                <Headline text={slide.headline} highlight={slide.highlight} />
              </h2>
              <Link to={`/services/${slide.slug}`} className='spot-link'>
                {t('spotExplore', 'Explore the service')}
                <span aria-hidden='true'>→</span>
              </Link>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className='spot-controls'>
          <button type='button' className='spot-arrow' onClick={() => go(-1)} aria-label={t('spotPrev', 'Previous slide')}>
            <span aria-hidden='true'>←</span>
          </button>
          <button type='button' className='spot-arrow' onClick={() => go(1)} aria-label={t('spotNext', 'Next slide')}>
            <span aria-hidden='true'>→</span>
          </button>
          <p className='spot-count'>
            <span className='spot-count-current'>{pad(index + 1)}</span> / {pad(count)}
          </p>
          {!reduced && (
            <div className='spot-progress' aria-hidden='true'>
              <span key={`${index}-${paused || !inView}`} className={paused || !inView ? '' : 'is-running'} />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ServiceSpotlight
